import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { InputPhoneComponent } from '@shared/input-phone/components';
import { ImporterOfRecordContactInformationModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { isIorMatchingUser } from '@helpers/ior-match.helper';

@Component({
  selector: 'app-poa-ior-edit-sidebar',
  templateUrl: './poa-ior-edit-sidebar.component.html',
  styleUrls: ['./poa-ior-edit-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoaIorEditSidebarComponent implements OnInit, OnDestroy {
  @ViewChild(InputPhoneComponent, { static: false }) public inputPhone: InputPhoneComponent;

  @Input() public iorData: ImporterOfRecordContactInformationModel;
  @Output() public saveSource$: EventEmitter<ImporterOfRecordContactInformationModel> = new EventEmitter();
  @Output() public confirmedAsUser$: EventEmitter<void> = new EventEmitter<void>();

  public formGroup: UntypedFormGroup;
  public isMatchConfirmOpen: boolean = false;

  public errors: { [name: string]: IErrorForm } = {
    firstName: { required: 'FORM_ERROR.REQUIRED' },
    lastName: { required: 'FORM_ERROR.REQUIRED' },
    phoneNumber: { required: 'FORM_ERROR.REQUIRED', validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER' },
    emailAddress: { required: 'FORM_ERROR.REQUIRED', invalidEmail: 'FORM_ERROR.LOGIN_EMAIL', email: 'FORM_ERROR.LOGIN_EMAIL' },
  };

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();
  private pendingPayload: ImporterOfRecordContactInformationModel | null = null;

  constructor(private cdr: ChangeDetectorRef, private companiesService: CompaniesService) {}

  public ngOnInit(): void {
    this.createForm();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get isFormValid(): boolean {
    const formValid = this.formGroup?.valid;
    const phoneValid = this.isPhoneValid();
    return formValid && phoneValid;
  }

  public trimField(fieldName: string): void {
    const control = this.formGroup?.get(fieldName);
    if (control && typeof control.value === 'string') {
      control.setValue(control.value.trim());
    }
  }

  public onEmailBlur(): void {
    const control = this.formGroup?.get('emailAddress');
    if (!control) {
      return;
    }

    const trimmedValue = (control.value || '').trim();
    control.setValue(trimmedValue);
    control.markAsTouched();
  }

  public onSave(): void {
    if (!this.isFormValid) {
      this.formGroup.markAllAsTouched();
      this.inputPhone?.inputFormControl?.markAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const phoneValue = this.formGroup.get('phoneNumber').value
      ? String(this.formGroup.get('phoneNumber').value).replace(/(\s|-)*/g, '')
      : '';

    const payload: ImporterOfRecordContactInformationModel = {
      firstName: (this.formGroup.get('firstName').value || '').trim(),
      lastName: (this.formGroup.get('lastName').value || '').trim(),
      phoneNumber: phoneValue,
      emailAddress: (this.formGroup.get('emailAddress').value || '').trim(),
    };

    if (isIorMatchingUser(payload, this.companiesService.getUser())) {
      this.pendingPayload = payload;
      this.isMatchConfirmOpen = true;
      this.cdr.markForCheck();
      return;
    }

    this.saveSource$.emit(payload);
  }

  public onIorMatchAccept(): void {
    this.isMatchConfirmOpen = false;
    this.pendingPayload = null;
    this.confirmedAsUser$.emit();
    this.cdr.markForCheck();
  }

  public onIorMatchDecline(): void {
    if (!this.isMatchConfirmOpen) {
      return;
    }
    this.isMatchConfirmOpen = false;
    const payload: ImporterOfRecordContactInformationModel | null = this.pendingPayload;
    this.pendingPayload = null;
    this.cdr.markForCheck();
    if (payload) {
      this.saveSource$.emit(payload);
    }
  }

  private createForm(): void {
    this.formGroup = new UntypedFormGroup({
      firstName: new UntypedFormControl(this.iorData?.firstName || '', [Validators.required]),
      lastName: new UntypedFormControl(this.iorData?.lastName || '', [Validators.required]),
      phoneNumber: new UntypedFormControl(this.iorData?.phoneNumber || '', [Validators.required]),
      emailAddress: new UntypedFormControl(this.iorData?.emailAddress || '', [Validators.required, Validators.email]),
    });
  }

  private isPhoneValid(): boolean {
    const phoneCtrl = this.formGroup?.get('phoneNumber');
    const hasValue = !!phoneCtrl?.value;
    const isInternalValid = this.inputPhone?.inputFormControl ? !this.inputPhone.inputFormControl.invalid : true;
    return hasValue && isInternalValid;
  }
}
